import { getBackendUrl, resolveAssetUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Star, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import SeoHead from '../components/SeoHead';

const EvaluationPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evaluationData, setEvaluationData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [platformName, setPlatformName] = useState('Talent Partner');
  
  const [scores, setScores] = useState({
    quality_score: 0,
    communication_score: 0,
    deadline_score: 0,
    recommendation_score: 0
  });
  const [comment, setComment] = useState('');

  useEffect(() => {
    // Fetch branding
    fetch(getBackendUrl() + '/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.logo_light_url) setLogoUrl(data.logo_light_url);
        if (data.platform_name) setPlatformName(data.platform_name);
      })
      .catch(err => console.error('Failed to fetch branding:', err));

    // Fetch evaluation data
    fetchEvaluation();
  }, [token]);

  const fetchEvaluation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/evaluations/${token}`);
      const data = await response.json();
      
      if (data.status === 'completed') {
        setSubmitted(true);
      }
      
      setEvaluationData(data);
    } catch (error) {
      console.error('Failed to fetch evaluation:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all scores are selected
    if (Object.values(scores).some(s => s === 0)) {
      toast.error('Veuillez attribuer une note à chaque critère');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/evaluations/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...scores,
          comment: comment || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erreur lors de l\'envoi de votre évaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ label, description, value, onChange }) => (
    <div className="mb-6">
      <Label className="text-base font-semibold text-[#111827]">{label}</Label>
      <p className="text-sm text-muted-foreground mb-3">{description}</p>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110 focus:outline-none"
          >
            <Star 
              className={`h-8 w-8 ${
                star <= value 
                  ? 'fill-amber-400 text-amber-400' 
                  : 'text-gray-300 hover:text-amber-200'
              } transition-colors`}
            />
          </button>
        ))}
        <span className="ml-3 text-sm text-muted-foreground">
          {value > 0 ? `${value}/5` : 'Non noté'}
        </span>
      </div>
    </div>
  );

  const seoTag = (
    <SeoHead
      pageTitle="Évaluation — Talent Partner"
      pageDescription="Page d'évaluation privée (accès par lien sécurisé)."
      noindex={true}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        {seoTag}
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!evaluationData || evaluationData.status === 'error') {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
        {seoTag}
        <nav className="bg-white border-b border-[#E5EAF1]">
          <div className="max-w-2xl mx-auto px-4 py-4 text-center">
            {logoUrl ? (
              <img 
                src={resolveAssetUrl(logoUrl)}
                alt={platformName}
                className="h-8 mx-auto object-contain"
              />
            ) : (
              <span className="text-xl font-display font-semibold text-[#1E3A5F]">
                {platformName}
              </span>
            )}
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <Card className="p-8 max-w-md text-center">
            <h1 className="text-xl font-display font-semibold text-[#111827] mb-2">
              Évaluation non trouvée
            </h1>
            <p className="text-muted-foreground">
              Ce lien d'évaluation n'est pas valide ou a expiré.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
        {seoTag}
        <nav className="bg-white border-b border-[#E5EAF1]">
          <div className="max-w-2xl mx-auto px-4 py-4 text-center">
            {logoUrl ? (
              <img 
                src={resolveAssetUrl(logoUrl)}
                alt={platformName}
                className="h-8 mx-auto object-contain"
              />
            ) : (
              <span className="text-xl font-display font-semibold text-[#1E3A5F]">
                {platformName}
              </span>
            )}
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-display font-semibold text-[#111827] mb-2">
              Merci pour votre évaluation !
            </h1>
            <p className="text-muted-foreground mb-6">
              Votre retour nous aide à améliorer la qualité de nos services et à accompagner nos talents.
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Retour au site
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
      {seoTag}
      {/* Header */}
      <nav className="bg-white border-b border-[#E5EAF1]">
        <div className="max-w-2xl mx-auto px-4 py-4 text-center">
          {logoUrl ? (
            <img 
              src={resolveAssetUrl(logoUrl)}
              alt={platformName}
              className="h-8 mx-auto object-contain"
            />
          ) : (
            <span className="text-xl font-display font-semibold text-[#1E3A5F]">
              {platformName}
            </span>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 py-8 md:py-12" data-testid="evaluation-page">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="p-6 md:p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-[#111827] mb-2">
                Évaluez votre collaboration
              </h1>
              <p className="text-muted-foreground">
                avec <span className="font-medium text-[#111827]">{evaluationData.talent_name}</span>
              </p>
              {evaluationData.talent_role && (
                <p className="text-sm text-muted-foreground mt-1">
                  {evaluationData.talent_role} • {evaluationData.mission_title}
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-2">
              <StarRating
                label="Qualité du travail"
                description="Le travail livré correspondait-il à vos attentes ?"
                value={scores.quality_score}
                onChange={(val) => setScores({ ...scores, quality_score: val })}
              />

              <StarRating
                label="Communication"
                description="Les échanges étaient-ils clairs et réactifs ?"
                value={scores.communication_score}
                onChange={(val) => setScores({ ...scores, communication_score: val })}
              />

              <StarRating
                label="Respect des délais"
                description="Les échéances ont-elles été respectées ?"
                value={scores.deadline_score}
                onChange={(val) => setScores({ ...scores, deadline_score: val })}
              />

              <StarRating
                label="Recommandation"
                description="Recommanderiez-vous ce talent à d'autres entreprises ?"
                value={scores.recommendation_score}
                onChange={(val) => setScores({ ...scores, recommendation_score: val })}
              />

              <div className="pt-4 border-t border-[#E5EAF1]">
                <Label htmlFor="comment" className="text-base font-semibold text-[#111827]">
                  Commentaire (optionnel)
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Partagez votre expérience pour aider à améliorer nos services
                </p>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Votre retour détaillé..."
                  rows={4}
                  className="resize-none"
                  data-testid="evaluation-comment"
                />
              </div>

              <div className="pt-6">
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--brand-800))]"
                  data-testid="submit-evaluation-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer mon évaluation'
                  )}
                </Button>
              </div>
            </form>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Votre évaluation est confidentielle et sera utilisée pour améliorer la qualité de nos services.
          </p>
        </div>
      </main>
    </div>
  );
};

export default EvaluationPage;
